import {inject, Injectable} from '@angular/core';
import {environment} from '@environments/environment';
import {IIframeAppConfiguration} from '../../../interfaces/IIframeAppConfiguration';
import {IWebComponentAppConfiguration} from '../../../interfaces/IWebComponentAppConfiguration';
import {EtkConfiguration} from '../../configuration/interfaces/etk-configuration';
import {LeadsConfiguration} from '../../configuration/interfaces/leads-configuration';
import {OutletRelatedConfiguration} from '../../configuration/interfaces/outlet-related-configuration';
import {ConfigurationLoader} from '../../configuration/services/configuration.loader';
import {ConfigurationService} from '../../configuration/services/configuration.service';
import {UserData} from '../../user/models/user-data';
import {UserService} from '../../user/services/user.service';

@Injectable({
  providedIn: 'root'
})
export class AppsService {
  private readonly userService: UserService = inject(UserService);
  private readonly configurationLoader: ConfigurationLoader = inject(ConfigurationLoader);

  private leadsDealerConfig: LeadsConfiguration;
  private user: UserData;

  constructor() {
    this.userService.user$.subscribe((user: UserData): void => {
      this.user = user;
    });

    this.configurationLoader.configChanged.subscribe((config: {
      outletRelatedConfig: OutletRelatedConfiguration,
      etkConfig: EtkConfiguration
    }): void => {
      this.leadsDealerConfig = config.outletRelatedConfig?.leadsDealerConfiguration;
    });
  }

  /**
   * Check if app is visible for current user based on roles, market config & environment configuration.
   * @param routingPath The routing path of the app to check, e.g. 'etk'.
   */
  appVisibleForUser(routingPath: string): boolean {
    let app: IIframeAppConfiguration | IWebComponentAppConfiguration = this.getAllApps()
      .find((app: IIframeAppConfiguration | IWebComponentAppConfiguration) => app.routingPath === routingPath);

    if (!this.user || !app) {
      return false;
    }

    return this.userHasRightsForApp(routingPath) &&
      this.appVisibleInContext(app) &&
      this.userHasMarketRole(app) &&
      this.appActiveInMarketConfiguration(app);
  }

  private userHasRightsForApp(routingPath: string) {
    const appConfig: IIframeAppConfiguration | IWebComponentAppConfiguration =
      this.getAllApps().find(app => (app.routingPath === routingPath));
    if (appConfig) {
      const requiredRoles: string[] = appConfig.b2dRoles;
      // return true if there are no roles required, check user roles otherwise
      return !requiredRoles
        || requiredRoles.length === 0
        || requiredRoles.some(requiredRole => this.user.getRoles().includes(requiredRole));
    } else {
      return false;
    }
  }

  getVisibleApps(): (IIframeAppConfiguration | IWebComponentAppConfiguration)[] {
    return this.getAllApps()
      .filter((app: IIframeAppConfiguration | IWebComponentAppConfiguration) => this.appVisibleForUser(app.routingPath));
  }

  getAllApps(): (IIframeAppConfiguration | IWebComponentAppConfiguration)[] {
    return environment.navigationItems
      .map(item => 'apps' in item ? item.apps : [item])
      .flat();
  }

  private appActiveInMarketConfiguration(app: IIframeAppConfiguration | IWebComponentAppConfiguration): boolean {
    const marketConfigFeature: string = app.marketConfigurationFeature;
    if (marketConfigFeature) {
      return !!this.leadsDealerConfig?.[marketConfigFeature];
    }
    return true;
  }

  private appVisibleInContext(app: IIframeAppConfiguration | IWebComponentAppConfiguration): boolean {
    const urls = (app as IIframeAppConfiguration).iFrameUrls || (app as IWebComponentAppConfiguration).webComponentUrls;
    return ConfigurationService.usesIntranetUrls() ? !!urls.b2dIntranet : !!urls.b2d;
  }

  private userHasMarketRole(app: IIframeAppConfiguration | IWebComponentAppConfiguration) {
    // return true if there are no market roles required, check user roles otherwise
    return !app.marketRoles
      || app.marketRoles.length === 0
      || app.marketRoles.some(requiredRole => this.user.getRoles().includes(requiredRole));
  }
}
